import { z } from "zod";
import { neighbourhoodKeySchema } from "./contracts";

const timestampSchema = z.string().datetime({ offset: true });
const nonNegativeInteger = z.number().int().nonnegative();
const positiveInteger = z.number().int().positive();

export const gatherableResourceKeySchema = z.enum(["food", "wood", "stone", "gold"]);
export const resourceNodeStateSchema = z.enum(["AVAILABLE", "OCCUPIED", "DEPLETED", "RETIRED"]);
export const gatheringSessionStateSchema = z.enum(["ACTIVE", "DISPLACED", "DEPLETED", "RETURNING", "COMPLETE", "CANCELLED"]);

export const resourceNodeDefinitionSchema = z.object({
  contractVersion: z.literal(1),
  id: z.string().uuid(),
  key: neighbourhoodKeySchema,
  name: z.string().min(1),
  resourceKey: gatherableResourceKeySchema,
  nodeAmount: positiveInteger,
  gatherRateNumerator: positiveInteger,
  gatherRateIntervalSeconds: positiveInteger,
  iconAssetId: z.string().uuid().nullable(),
  productionEnabled: z.boolean(),
  archived: z.boolean(),
}).strict();

export const resourceSpawnPolicySchema = z.object({
  contractVersion: z.literal(1),
  targetActiveMin: positiveInteger,
  targetActiveMax: positiveInteger,
  vacancyRefreshSeconds: positiveInteger,
  partialExpirySeconds: positiveInteger,
  fairnessMode: z.literal("sector_round_robin"),
  productionEnabled: z.boolean(),
}).strict().superRefine((policy, context) => {
  if (policy.targetActiveMin > policy.targetActiveMax) {
    context.addIssue({ code: "custom", path: ["targetActiveMax"], message: "Maximum active nodes cannot be below the minimum." });
  }
});

export const resourceNodeInstanceSchema = z.object({
  contractVersion: z.literal(1),
  id: z.string().uuid(),
  neighbourhoodId: z.string().uuid(),
  locationId: z.string().uuid(),
  definitionId: z.string().uuid(),
  generation: positiveInteger,
  resourceKey: gatherableResourceKeySchema,
  initialAmount: positiveInteger,
  remainingAmount: nonNegativeInteger,
  state: resourceNodeStateSchema,
  spawnedAt: timestampSchema,
  expiresAt: timestampSchema,
  occupiedByMarchId: z.string().uuid().nullable(),
  stateVersion: positiveInteger,
}).strict().superRefine((node, context) => {
  if (node.remainingAmount > node.initialAmount) {
    context.addIssue({ code: "custom", path: ["remainingAmount"], message: "Remaining amount cannot exceed the captured initial amount." });
  }
  if ((node.state === "OCCUPIED") !== (node.occupiedByMarchId !== null)) {
    context.addIssue({ code: "custom", path: ["occupiedByMarchId"], message: "Only an occupied node may reference an occupying march." });
  }
  if (node.state === "DEPLETED" && node.remainingAmount !== 0) {
    context.addIssue({ code: "custom", path: ["remainingAmount"], message: "A depleted node must have zero remaining amount." });
  }
});

export const marchCargoSchema = z.object({
  marchId: z.string().uuid(),
  resourceKey: gatherableResourceKeySchema,
  amount: nonNegativeInteger,
  loadCapacity: nonNegativeInteger,
  creditedAt: timestampSchema.nullable(),
}).strict().superRefine((cargo, context) => {
  if (cargo.amount > cargo.loadCapacity) {
    context.addIssue({ code: "custom", path: ["amount"], message: "Cargo cannot exceed the captured remaining Load." });
  }
});

export type GatherableResourceKey = z.infer<typeof gatherableResourceKeySchema>;
export type ResourceNodeDefinitionV1 = z.infer<typeof resourceNodeDefinitionSchema>;
export type ResourceSpawnPolicyV1 = z.infer<typeof resourceSpawnPolicySchema>;
export type ResourceNodeInstanceV1 = z.infer<typeof resourceNodeInstanceSchema>;
export type MarchCargoV1 = z.infer<typeof marchCargoSchema>;

function nonNegativeSafeInteger(value: number, label: string) {
  if (!Number.isSafeInteger(value) || value < 0) throw new RangeError(`${label} must be a nonnegative safe integer.`);
}

export type GatheredAmountInput = {
  startedAt: string;
  now: string;
  rateNumerator: number;
  rateIntervalSeconds: number;
  nodeAmountAtStart: number;
  remainingLoadAtStart: number;
};

export function gatheredAmountAt(input: GatheredAmountInput) {
  nonNegativeSafeInteger(input.rateNumerator, "Gather rate numerator");
  nonNegativeSafeInteger(input.rateIntervalSeconds, "Gather rate interval");
  nonNegativeSafeInteger(input.nodeAmountAtStart, "Node amount");
  nonNegativeSafeInteger(input.remainingLoadAtStart, "Remaining Load");
  if (input.rateNumerator === 0 || input.rateIntervalSeconds === 0) throw new RangeError("Gather rate must be positive.");
  const elapsedMilliseconds = Math.max(0, Date.parse(input.now) - Date.parse(input.startedAt));
  if (!Number.isFinite(elapsedMilliseconds)) throw new RangeError("Gather timestamps must be valid.");
  const elapsedSeconds = BigInt(Math.floor(elapsedMilliseconds / 1_000));
  const produced = (elapsedSeconds * BigInt(input.rateNumerator)) / BigInt(input.rateIntervalSeconds);
  const capped = [produced, BigInt(input.nodeAmountAtStart), BigInt(input.remainingLoadAtStart)]
    .reduce((minimum, value) => value < minimum ? value : minimum);
  const result = Number(capped);
  if (!Number.isSafeInteger(result)) throw new RangeError("Gathered amount exceeds the safe integer range.");
  return result;
}

export function compareResourceArrivals(
  left: Pick<{ arrivesAt: string; marchId: string }, "arrivesAt" | "marchId">,
  right: Pick<{ arrivesAt: string; marchId: string }, "arrivesAt" | "marchId">,
) {
  const timestampOrder = Date.parse(left.arrivesAt) - Date.parse(right.arrivesAt);
  return timestampOrder || left.marchId.localeCompare(right.marchId);
}

export function canExpireResourceNode(node: Pick<ResourceNodeInstanceV1, "state" | "occupiedByMarchId" | "expiresAt">, now: string) {
  return node.state === "AVAILABLE" && node.occupiedByMarchId === null && Date.parse(node.expiresAt) <= Date.parse(now);
}
